import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import {
  Users,
  UserPlus,
  Search,
  Trash2,
  Edit2,
  Mail,
  Shield,
  RefreshCw,
  ChevronsDown,
  X,
  User,
  CheckCircle2,
} from 'lucide-react';

import { DataState } from '../../context/DataProvider';
import { API_URL } from '../../services/api';
import { appImages } from '../../utils/constants';

// --- Compact Styles for React Select ---
const selectStyles = {
  control: (styles) => ({
    ...styles,
    backgroundColor: '#27272a', // zinc-800
    borderColor: '#3f3f46', // zinc-700
    boxShadow: 'none',
    '&:hover': { borderColor: '#52525b' }, // zinc-600
    minHeight: '36px', // Compact height
    borderRadius: '0.375rem', // rounded-md
    fontSize: '0.875rem',
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: '#18181b', // zinc-950
    borderColor: '#3f3f46',
    borderWidth: '1px',
    borderRadius: '0.375rem',
    padding: '0.25rem',
    zIndex: 50,
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected ? '#2563eb' : isFocused ? '#27272a' : '#18181b',
    color: 'white',
    cursor: 'pointer',
    fontSize: '0.75rem', // Smaller font
    padding: '0.375rem 0.75rem',
    borderRadius: '0.25rem',
  }),
  singleValue: (styles) => ({
    ...styles,
    color: '#e4e4e7',
    fontSize: '0.875rem',
  }),
  input: (styles) => ({ ...styles, color: 'white', margin: 0, padding: 0 }),
  valueContainer: (styles) => ({ ...styles, padding: '0 8px' }),
  placeholder: (styles) => ({
    ...styles,
    color: '#a1a1aa',
    fontSize: '0.875rem',
  }),
};

// --- UI Helper Components ---

const Avatar = ({ name }) => {
  const initials = name
    ? name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase()
    : '??';

  const colors = [
    'bg-blue-600',
    'bg-emerald-600',
    'bg-purple-600',
    'bg-amber-600',
    'bg-rose-600',
  ];
  const colorClass = colors[name ? name.length % colors.length : 0];

  return (
    <div
      className={`h-8 w-8 rounded-full ${colorClass} flex items-center justify-center text-xs font-bold text-white shadow-sm ring-1 ring-zinc-900`}
    >
      {initials}
    </div>
  );
};

const AppBadge = ({ appKey, data }) => {
  if (!data) return null;
  const appNames = {
    jira: 'Jira',
    microsoft_teams: 'Teams',
    slack: 'Slack',
    gmail: 'Gmail',
  };

  return (
    <div className="flex items-center gap-1.5 rounded border border-zinc-700/50 bg-zinc-800/80 px-2 py-0.5 text-[11px] text-zinc-300">
      {appImages[appKey] ? (
        <img
          src={appImages[appKey]}
          alt={appKey}
          className="h-3 w-3 object-contain"
        />
      ) : (
        <div className="h-1.5 w-1.5 rounded-full bg-[#5652CF]"></div>
      )}
      <span className="max-w-[100px] truncate">
        {data.displayName || data.email || appNames[appKey]}
      </span>
    </div>
  );
};

const UserTableRowSkeleton = () => (
  <tr className="animate-pulse border-b border-zinc-800/50">
    <td className="px-6 py-3">
      <div className="h-8 w-8 rounded-full bg-zinc-800"></div>
    </td>
    <td className="px-6 py-3">
      <div className="mb-1 h-4 w-32 rounded bg-zinc-800"></div>
      <div className="h-3 w-20 rounded bg-zinc-800"></div>
    </td>
    <td className="px-6 py-3">
      <div className="flex gap-2">
        <div className="h-6 w-16 rounded bg-zinc-800"></div>
      </div>
    </td>
    <td className="px-6 py-3 text-right">
      <div className="ml-auto h-7 w-7 rounded bg-zinc-800"></div>
    </td>
  </tr>
);

// --- Main Component ---

const UserManagementContent = () => {
  const { userGoogleId } = DataState();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isBulkMapOpen, setIsBulkMapOpen] = useState(false);
  const [mappableAccounts, setMappableAccounts] = useState({
    jira_users: [],
    teams_users: [],
  });

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [usersRes, mappableRes] = await Promise.all([
        fetch(`${API_URL}/users/`, {
          headers: { Authorization: `Bearer ${userGoogleId}` },
          credentials: 'include',
        }),
        fetch(`${API_URL}/users/mappable_accounts`, {
          headers: { Authorization: `Bearer ${userGoogleId}` },
          credentials: 'include',
        }),
      ]);

      if (!usersRes.ok) throw new Error('Failed to load user list.');

      const usersData = await usersRes.json();
      const mappableData = mappableRes.ok
        ? await mappableRes.json()
        : { jira_users: [], teams_users: [] };

      setUsers(usersData);
      setMappableAccounts(mappableData);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGoogleId]);

  const handleAddUser = () => {
    setEditingUser({ displayName: '', email: '', role: '', app_mappings: {} });
    setIsFormOpen(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setIsFormOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Delete this user? This cannot be undone.')) {
      try {
        const res = await fetch(`${API_URL}/users/${userId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${userGoogleId}` },
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to delete user.');
        toast.success('User deleted.');
        fetchAllData();
      } catch (err) {
        toast.error(err.message);
      }
    }
  };

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;
    const lowerQuery = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        (user.displayName &&
          user.displayName.toLowerCase().includes(lowerQuery)) ||
        (user.email && user.email.toLowerCase().includes(lowerQuery)),
    );
  }, [users, searchQuery]);

  return (
    <div className="flex h-full max-h-screen flex-col p-1">
      {/* --- Compact Header --- */}
      <div className="mb-4 flex flex-col items-center justify-between gap-4 px-1 md:flex-row">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-bold text-zinc-100">
            <Users className="h-5 w-5 text-[#5652CF]" />
            Team Directory
          </h3>
          <p className="text-xs text-zinc-500">
            Manage user access and integrations.
          </p>
        </div>

        <div className="flex w-full items-center gap-2 md:w-auto">
          <button
            onClick={() => fetchAllData()}
            disabled={loading}
            className="rounded-md border border-zinc-700 bg-zinc-800/50 p-2 text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-100 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <button
            onClick={() => setIsBulkMapOpen(true)}
            className="flex items-center gap-2 rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-xs font-medium text-zinc-200 transition-all hover:border-zinc-600 hover:bg-zinc-700"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Bulk Sync
          </button>

          <button
            onClick={handleAddUser}
            className="flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-[#5652CF]"
          >
            <UserPlus className="h-3.5 w-3.5" /> Add User
          </button>
        </div>
      </div>

      {/* --- Content Table Card --- */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-zinc-800 bg-[#121214] shadow-sm">
        {/* Compact Toolbar */}
        <div className="flex items-center gap-4 border-b border-zinc-800 bg-zinc-900/30 px-4 py-3">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-md border border-zinc-700 bg-zinc-900 py-2 pl-9 pr-3 text-xs text-zinc-200 transition-all placeholder:text-zinc-600 focus:border-[#5652CF] focus:outline-none focus:ring-1 focus:ring-[#5652CF]/50"
            />
          </div>
          <div className="ml-auto hidden text-xs text-zinc-500 md:block">
            <span className="font-semibold text-zinc-300">{users.length}</span>{' '}
            Users
          </div>
        </div>

        {/* Compact Table */}
        <div className="custom-scrollbar flex-1 overflow-auto bg-zinc-900/10">
          <table className="w-full border-collapse text-left">
            <thead className="sticky top-0 z-10 border-b border-zinc-800 bg-zinc-900/90 backdrop-blur-sm">
              <tr>
                <th className="w-14 px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  Avatar
                </th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  User Details
                </th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  Integrations
                </th>
                <th className="px-6 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/50">
              {loading ? (
                <>
                  <UserTableRowSkeleton />
                  <UserTableRowSkeleton />
                  <UserTableRowSkeleton />
                </>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="4" className="py-16 text-center">
                    <div className="flex flex-col items-center justify-center text-zinc-500">
                      <div className="mb-3 rounded-full bg-zinc-800/50 p-3">
                        <User className="h-6 w-6 opacity-50" />
                      </div>
                      <p className="text-sm font-medium text-zinc-400">
                        No users found
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="group transition-colors hover:bg-zinc-800/30"
                  >
                    <td className="px-6 py-3 align-top">
                      <Avatar name={user.displayName} />
                    </td>
                    <td className="px-6 py-3 align-top">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium leading-tight text-zinc-200 transition-colors group-hover:text-blue-400">
                          {user.displayName || 'Unnamed'}
                        </span>
                        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-zinc-500">
                          <Mail className="h-3 w-3" />
                          {user.email}
                        </div>
                        {user.role && (
                          <div className="mt-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-zinc-600">
                            <Shield className="h-3 w-3" /> {user.role}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3 align-top">
                      <div className="flex flex-wrap gap-1.5">
                        {Object.keys(user.app_mappings || {}).length === 0 && (
                          <span className="text-xs italic text-zinc-600">
                            No links
                          </span>
                        )}
                        {Object.entries(user.app_mappings || {}).map(
                          ([key, data]) => (
                            <AppBadge key={key} appKey={key} data={data} />
                          ),
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-right align-middle">
                      <div className="flex items-center justify-end gap-1 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="rounded-md p-1.5 text-zinc-400 transition-all hover:bg-[#5652CF]/10 hover:text-blue-400"
                          title="Edit"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="rounded-md p-1.5 text-zinc-400 transition-all hover:bg-red-500/10 hover:text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Modals --- */}
      {isFormOpen && (
        <UserForm
          user={editingUser}
          allUsers={users}
          mappableAccounts={mappableAccounts}
          onClose={() => setIsFormOpen(false)}
          onSave={() => {
            setIsFormOpen(false);
            fetchAllData();
          }}
        />
      )}

      {isBulkMapOpen && (
        <BulkMapModal
          allUsers={users}
          mappableTeamsUsers={mappableAccounts.teams_users}
          onClose={() => setIsBulkMapOpen(false)}
          onSave={() => {
            setIsBulkMapOpen(false);
            fetchAllData();
          }}
        />
      )}
    </div>
  );
};

// --- Compact User Form Modal ---
const UserForm = ({ user, allUsers, onClose, onSave, mappableAccounts }) => {
  const { userGoogleId } = DataState();
  const [formData, setFormData] = useState(user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Generate role options
  const standardRoles = [
    'Developer',
    'Project Manager',
    'QA Analyst',
    'Designer',
    'Admin',
  ];
  const existingRoles = [
    ...new Set(allUsers.map((u) => u.role).filter(Boolean)),
  ];
  const roleOptions = [...new Set([...standardRoles, ...existingRoles])].map(
    (role) => ({ value: role, label: role }),
  );

  useEffect(() => {
    if (!formData.email) {
      setEmailError('');
      return;
    }
    const isDuplicate = allUsers.some(
      (existingUser) =>
        existingUser.email.toLowerCase() === formData.email.toLowerCase() &&
        existingUser.id !== user.id,
    );
    if (isDuplicate) {
      setEmailError('Email already exists.');
    } else {
      setEmailError('');
    }
  }, [formData.email, allUsers, user.id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRoleChange = (selectedOption) => {
    setFormData({
      ...formData,
      role: selectedOption ? selectedOption.value : '',
    });
  };

  const handleMappingChange = (appName, selectedOption) => {
    const newMappings = { ...formData.app_mappings };
    if (selectedOption) {
      const originalUser = selectedOption.originalUser;
      if (appName === 'jira') {
        newMappings.jira = {
          accountId: originalUser.accountId,
          displayName: originalUser.displayName,
          emailAddress: originalUser.emailAddress,
        };
      } else if (appName === 'microsoft_teams') {
        newMappings.microsoft_teams = {
          userId: originalUser.userId,
          displayName: originalUser.displayName,
          email: originalUser.email,
        };
      }
    } else {
      delete newMappings[appName];
    }
    setFormData({ ...formData, app_mappings: newMappings });
  };

  const handlePrefill = (source) => {
    let prefillData = { displayName: '', email: '' };

    if (source === 'jira' && formData.app_mappings.jira) {
      const jiraUser = mappableAccounts.jira_users.find(
        (u) => u.accountId === formData.app_mappings.jira.accountId,
      );
      if (jiraUser) {
        prefillData.displayName = jiraUser.displayName;
        prefillData.email = jiraUser.emailAddress || '';
      }
    } else if (
      source === 'microsoft_teams' &&
      formData.app_mappings.microsoft_teams
    ) {
      const teamsUser = mappableAccounts.teams_users.find(
        (u) => u.userId === formData.app_mappings.microsoft_teams.userId,
      );
      if (teamsUser) {
        prefillData.displayName = teamsUser.displayName;
        prefillData.email = teamsUser.email || '';
      }
    }

    setFormData((prev) => ({
      ...prev,
      displayName: prefillData.displayName,
      email: prefillData.email,
    }));
    toast.success(`Form pre-filled.`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const url = user.id ? `${API_URL}/users/${user.id}` : `${API_URL}/users/`;
    const method = user.id ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userGoogleId}`,
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Failed to save user.');
      toast.success(`User saved.`);
      onSave();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const jiraOptions = mappableAccounts.jira_users.map((u) => ({
    value: u.accountId,
    label: `${u.displayName} (${u.emailAddress || 'No Email'})`,
    originalUser: u,
  }));

  const teamsOptions = mappableAccounts.teams_users.map((u) => ({
    value: u.userId,
    label: `${u.displayName} (${u.email || 'No Email'})`,
    originalUser: u,
  }));

  return (
    <div className="animate-in fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm duration-200">
      <div className="animate-in zoom-in-95 flex w-full max-w-md scale-100 flex-col rounded-lg border border-zinc-700 bg-[#121214] p-6 shadow-2xl">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-lg font-bold text-zinc-100">
            {user.id ? 'Edit User' : 'Add User'}
          </h3>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-3">
            <div>
              <label
                htmlFor="display-name"
                className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-zinc-400"
              >
                Display Name
              </label>
              <input
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                placeholder="Name"
                className="w-full rounded-md border border-zinc-700 bg-zinc-900 p-2 text-sm text-white transition-all focus:border-[#5652CF] focus:outline-none focus:ring-1 focus:ring-[#5652CF]/50"
                required
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-zinc-400"
              >
                Email
              </label>
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
                className={`w-full border bg-zinc-900 ${emailError ? 'border-red-500' : 'border-zinc-700'} rounded-md p-2 text-sm text-white transition-all focus:border-[#5652CF] focus:outline-none focus:ring-1`}
                required
              />
              {emailError && (
                <p className="mt-1 text-xs text-red-400">{emailError}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="role"
                className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-zinc-400"
              >
                Role
              </label>
              <CreatableSelect
                styles={selectStyles}
                options={roleOptions}
                value={
                  formData.role
                    ? { value: formData.role, label: formData.role }
                    : null
                }
                onChange={handleRoleChange}
                placeholder="Select role..."
                isClearable
              />
            </div>

            <div className="border-t border-zinc-800/50 pt-4">
              <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-zinc-300">
                Linked Accounts
              </h4>

              <div className="space-y-3">
                <div>
                  <div className="mb-1 block flex items-center gap-1.5 text-xs text-zinc-500">
                    <img
                      alt="Jira"
                      src={appImages.jira}
                      className="h-3.5 w-3.5 object-contain"
                    />{' '}
                    Jira
                  </div>
                  <Select
                    styles={selectStyles}
                    options={jiraOptions}
                    isClearable
                    value={jiraOptions.find(
                      (o) => o.value === formData.app_mappings.jira?.accountId,
                    )}
                    onChange={(opt) => handleMappingChange('jira', opt)}
                    placeholder="Search..."
                  />
                  {formData.app_mappings.jira && (
                    <button
                      type="button"
                      onClick={() => handlePrefill('jira')}
                      className="mt-1 flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300"
                    >
                      <ChevronsDown className="h-3 w-3" /> Prefill
                    </button>
                  )}
                </div>

                <div>
                  <div className="mb-1 block flex items-center gap-1.5 text-xs text-zinc-500">
                    <img
                      alt="Microsoft Teams"
                      src={appImages.microsoft_teams}
                      className="h-3.5 w-3.5 object-contain"
                    />{' '}
                    Teams
                  </div>
                  <Select
                    styles={selectStyles}
                    options={teamsOptions}
                    isClearable
                    value={teamsOptions.find(
                      (o) =>
                        o.value ===
                        formData.app_mappings.microsoft_teams?.userId,
                    )}
                    onChange={(opt) =>
                      handleMappingChange('microsoft_teams', opt)
                    }
                    placeholder="Search..."
                  />
                  {formData.app_mappings.microsoft_teams && (
                    <button
                      type="button"
                      onClick={() => handlePrefill('microsoft_teams')}
                      className="mt-1 flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300"
                    >
                      <ChevronsDown className="h-3 w-3" /> Prefill
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2 border-t border-zinc-800 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-xs font-semibold text-zinc-300 transition-colors hover:bg-zinc-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !!emailError}
              className="rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white transition-all hover:bg-[#5652CF] disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Compact Bulk Map Modal ---
const BulkMapModal = ({ allUsers, mappableTeamsUsers, onClose, onSave }) => {
  const { userGoogleId } = DataState();
  const [candidates, setCandidates] = useState([]);
  const [defaultRole, setDefaultRole] = useState('Developer');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    const existingEmails = new Set(allUsers.map((u) => u.email.toLowerCase()));
    const existingTeamsIds = new Set(
      allUsers
        .map((u) => u.app_mappings.microsoft_teams?.userId)
        .filter(Boolean),
    );

    const newCandidates = mappableTeamsUsers
      .filter(
        (teamsUser) =>
          teamsUser.email &&
          !existingEmails.has(teamsUser.email.toLowerCase()) &&
          !existingTeamsIds.has(teamsUser.userId),
      )
      .map((teamsUser) => ({
        displayName: teamsUser.displayName,
        email: teamsUser.email,
        role: defaultRole,
        app_mappings: {
          microsoft_teams: {
            userId: teamsUser.userId,
            displayName: teamsUser.displayName,
            email: teamsUser.email,
          },
        },
      }));
    setCandidates(newCandidates);
    // eslint-disable-next-line
  }, [allUsers, mappableTeamsUsers]);

  const handleCandidateChange = (index, field, value) => {
    const newCandidates = [...candidates];
    newCandidates[index][field] = value;
    setCandidates(newCandidates);
  };

  const handleDefaultRoleChange = (e) => {
    const newRole = e.target.value;
    setDefaultRole(newRole);
    setCandidates(candidates.map((c) => ({ ...c, role: newRole })));
  };

  const handleRemoveCandidate = (index) => {
    setCandidates(candidates.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setValidationErrors([]);
    try {
      const res = await fetch(`${API_URL}/users/bulk-map-teams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userGoogleId}`,
        },
        credentials: 'include',
        body: JSON.stringify(candidates),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error('Validation failed.');
        setValidationErrors(data.detail || []);
        throw new Error('Validation failed');
      }
      toast.success(data.message);
      onSave();
    } catch (err) {
      // Error handled
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="animate-in fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm duration-200">
      <div className="animate-in zoom-in-95 flex max-h-[90vh] w-full max-w-4xl scale-100 flex-col rounded-xl border border-zinc-700 bg-[#121214] shadow-2xl">
        <div className="flex items-center justify-between rounded-t-xl border-b border-zinc-800 bg-zinc-900/50 p-4">
          <div>
            <h3 className="text-lg font-bold text-zinc-100">Bulk Sync</h3>
            <p className="text-xs text-zinc-500">
              Found {candidates.length} potential users.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-center gap-3 border-b border-zinc-800 bg-zinc-900/30 p-3">
          <label
            htmlFor="role"
            className="whitespace-nowrap text-xs font-medium text-zinc-400"
          >
            Default Role:
          </label>
          <input
            value={defaultRole}
            onChange={handleDefaultRoleChange}
            className="w-48 rounded-md border border-zinc-700 bg-zinc-800 px-2 py-1 text-xs text-white transition-colors focus:border-[#5652CF] focus:outline-none"
          />
        </div>

        <div className="custom-scrollbar flex-grow overflow-auto p-0">
          <table className="w-full border-collapse text-left text-xs">
            <thead className="sticky top-0 z-10 bg-zinc-900">
              <tr>
                <th className="px-4 py-2 font-semibold uppercase tracking-wider text-zinc-500">
                  Display Name
                </th>
                <th className="px-4 py-2 font-semibold uppercase tracking-wider text-zinc-500">
                  Email
                </th>
                <th className="px-4 py-2 font-semibold uppercase tracking-wider text-zinc-500">
                  Role
                </th>
                <th className="px-4 py-2 text-center font-semibold uppercase tracking-wider text-zinc-500">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {candidates.length === 0 ? (
                <tr>
                  <td colSpan="4" className="p-8 text-center text-zinc-500">
                    No new candidates found.
                  </td>
                </tr>
              ) : (
                candidates.map((candidate, index) => {
                  const error = validationErrors.find((e) => e.index === index);
                  return (
                    <React.Fragment
                      key={candidate.app_mappings.microsoft_teams.userId}
                    >
                      <tr className="group transition-colors hover:bg-zinc-800/30">
                        <td className="p-2 pl-4">
                          <input
                            value={candidate.displayName}
                            onChange={(e) =>
                              handleCandidateChange(
                                index,
                                'displayName',
                                e.target.value,
                              )
                            }
                            className="w-full rounded border border-transparent bg-zinc-800/50 px-2 py-1 text-zinc-200 transition-colors focus:border-zinc-600 focus:outline-none"
                          />
                        </td>
                        <td className="p-2">
                          <input
                            value={candidate.email}
                            readOnly
                            className="w-full cursor-not-allowed bg-transparent px-2 py-1 text-zinc-500"
                          />
                        </td>
                        <td className="p-2">
                          <input
                            value={candidate.role}
                            onChange={(e) =>
                              handleCandidateChange(
                                index,
                                'role',
                                e.target.value,
                              )
                            }
                            className="w-full rounded border border-transparent bg-zinc-800/50 px-2 py-1 text-zinc-200 transition-colors focus:border-zinc-600 focus:outline-none"
                          />
                        </td>
                        <td className="p-2 text-center">
                          <button
                            onClick={() => handleRemoveCandidate(index)}
                            className="rounded p-1 text-zinc-500 transition-colors hover:bg-red-500/10 hover:text-red-400"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                      {error && (
                        <tr className="border-b border-red-900/20 bg-red-900/10">
                          <td
                            colSpan="4"
                            className="flex items-center gap-2 px-4 py-1 text-[10px] text-red-400"
                          >
                            <X className="h-3 w-3" /> {error.errors.join(' ')}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between rounded-b-xl border-t border-zinc-800 bg-zinc-900 p-4">
          <p className="text-xs font-medium text-zinc-500">
            {candidates.length} users to create.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-xs font-semibold text-zinc-300 transition-colors hover:bg-zinc-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || candidates.length === 0}
              className="flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white transition-all hover:bg-[#5652CF] disabled:opacity-50"
            >
              {isSubmitting ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5" />
              )}
              {isSubmitting ? 'Processing...' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagementContent;
